import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Fab,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  QrCode as QrCodeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import {
  fetchMachines,
  setSearchQuery,
  setFilters,
  deleteMachine,
} from '../../store/slices/machinesSlice';
import { Machine } from '../../types/machine';
import { format, isValid, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';

const statusColors = {
  active: 'success',
  maintenance: 'warning', 
  inactive: 'error',
} as const;

const statusLabels = {
  active: 'Aktiv',
  maintenance: 'Wartung',
  inactive: 'Inaktiv',
};

export const MachinesPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { machines, isLoading, searchQuery, filters, pagination } = useAppSelector((state) => state.machines);
  const { user } = useAppSelector((state) => state.auth);
  
  const [allLocations, setAllLocations] = useState<string[]>([]);
  const [machineToDelete, setMachineToDelete] = useState<Machine | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch all machines once to get unique locations
  useEffect(() => {
    dispatch(fetchMachines({ page: 1, limit: 1000 })).then((result: any) => {
      if (result.payload?.data) {
        const uniqueLocations = Array.from(new Set(result.payload.data.map((m: Machine) => m.location))).filter(Boolean);
        setAllLocations(uniqueLocations as string[]);
      }
    });
  }, [dispatch]);

  // Fetch filtered machines based on current filters
  useEffect(() => {
    dispatch(fetchMachines({
      page: pagination.page,
      limit: pagination.limit,
      search: searchQuery,
      ...filters,
    }));
  }, [dispatch, pagination.page, pagination.limit, searchQuery, filters]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSearchQuery(event.target.value));
  };

  const handleStatusFilter = (event: any) => {
    dispatch(setFilters({ status: event.target.value || undefined }));
  };

  const handleLocationFilter = (event: any) => {
    dispatch(setFilters({ location: event.target.value || undefined }));
  };

  const handleViewMachine = (id: string) => {
    navigate(`/machines/${id}`);
  };

  const handleEditMachine = (id: string) => {
    navigate(`/machines/${id}/edit`);
  };

  const handleAddMachine = () => {
    navigate('/machines/new');
  };

  const handleShowQR = (id: string) => {
    navigate(`/machines/${id}/qr`);
  };

  const handleDeleteRequest = (machine: Machine) => {
    setMachineToDelete(machine);
    setDeleteError(null);
  };

  const handleDeleteCancel = () => {
    if (isDeleting) {
      return;
    }
    setMachineToDelete(null);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!machineToDelete) {
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);
    try {
      await dispatch(deleteMachine(machineToDelete.id)).unwrap();
      setMachineToDelete(null);
    } catch (error: any) {
      setDeleteError(typeof error === 'string' ? error : 'Löschen der Maschine fehlgeschlagen');
    } finally {
      setIsDeleting(false);
    }
  };

  const canEdit = user?.role === 'admin';

  const renderMachineCard = (machine: Machine) => (
    <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={machine.id}>
      <Card
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: (theme) => theme.shadows[8],
          },
        }}
      >
        <CardMedia
          sx={{
            height: 200,
            position: 'relative',
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {machine.imageUrl ? (
            <img
              src={machine.thumbnailUrl || machine.imageUrl}
              alt={machine.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ) : (
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                fontSize: '2rem',
              }}
            >
              {machine.name.charAt(0).toUpperCase()}
            </Avatar>
          )}
          <Chip
            label={statusLabels[machine.status]}
            color={statusColors[machine.status]}
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
            }}
          />
        </CardMedia>
        
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography gutterBottom variant="h6" component="h2" noWrap>
            {machine.name}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            <strong>Standort:</strong> {machine.location}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {machine.description}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Erstellt: {(() => {
              if (!machine.createdAt) return 'Unbekannt';
              try {
                const date = parseISO(machine.createdAt);
                if (!isValid(date)) return 'Unbekannt';
                return format(date, 'dd.MM.yyyy', { locale: de });
              } catch {
                return 'Unbekannt';
              }
            })()}
          </Typography>
        </CardContent>
        
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          <Box>
            <IconButton
              size="small"
              onClick={() => handleViewMachine(machine.id)}
              color="primary"
            >
              <ViewIcon />
            </IconButton>
            {canEdit && (
              <IconButton
                size="small"
                onClick={() => handleEditMachine(machine.id)}
                color="secondary"
              >
                <EditIcon />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => handleShowQR(machine.id)}
              color="default"
            >
              <QrCodeIcon />
            </IconButton>
            {canEdit && (
              <IconButton
                size="small"
                onClick={() => handleDeleteRequest(machine)}
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleViewMachine(machine.id)}
          >
            Details
          </Button>
        </CardActions>
      </Card>
    </Grid>
  );

  const renderSkeletonCards = () => 
    Array.from({ length: 8 }).map((_, index) => (
      <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={`skeleton-${index}`}>
        <Card sx={{ height: '100%' }}>
          <Skeleton variant="rectangular" height={200} />
          <CardContent>
            <Skeleton variant="text" height={32} width="80%" />
            <Skeleton variant="text" height={20} width="60%" sx={{ mb: 1 }} />
            <Skeleton variant="text" height={16} />
            <Skeleton variant="text" height={16} />
            <Skeleton variant="text" height={16} width="40%" />
          </CardContent>
          <CardActions>
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="circular" width={40} height={40} />
            <Skeleton variant="rectangular" width={80} height={32} />
          </CardActions>
        </Card>
      </Grid>
    ));

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          Maschinen
        </Typography>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMachine}
          >
            Maschine hinzufügen
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              placeholder="Maschinen durchsuchen..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status || ''}
                label="Status"
                onChange={handleStatusFilter}
              >
                <MenuItem value="">Alle Status</MenuItem>
                <MenuItem value="active">Aktiv</MenuItem>
                <MenuItem value="maintenance">Wartung</MenuItem>
                <MenuItem value="inactive">Inaktiv</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <FormControl fullWidth>
              <InputLabel>Standort</InputLabel>
              <Select
                value={filters.location || ''}
                label="Standort"
                onChange={handleLocationFilter}
              >
                <MenuItem value="">Alle Standorte</MenuItem>
                {allLocations.map((location) => (
                  <MenuItem key={location} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      {/* Results Count */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {isLoading 
            ? 'Laden...' 
            : `${pagination.total} Maschine${pagination.total !== 1 ? 'n' : ''} gefunden`
          }
        </Typography>
      </Box>

      {/* Machines Grid */}
      <Grid container spacing={3}>
        {isLoading ? renderSkeletonCards() : machines.map(renderMachineCard)}
      </Grid>

      {/* Empty State */}
      {!isLoading && machines.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          py={8}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Keine Maschinen gefunden
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {searchQuery || filters.status || filters.location
              ? 'Versuchen Sie, Ihre Suchfilter anzupassen.'
              : 'Es sind noch keine Maschinen vorhanden.'}
          </Typography>
          {canEdit && !searchQuery && !filters.status && !filters.location && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddMachine}
            >
              Erste Maschine hinzufügen
            </Button>
          )}
        </Box>
      )}
      
      {/* Floating Action Button for Mobile */}
      {canEdit && (
        <Fab
          color="primary"
          aria-label="Maschine hinzufügen"
          onClick={handleAddMachine}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            display: { xs: 'flex', md: 'none' },
          }}
        >
          <AddIcon />
        </Fab>
      )}

      <Dialog
        open={Boolean(machineToDelete)}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Maschine löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Möchten Sie die Maschine "${machineToDelete?.name ?? ''}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
          </DialogContentText>
          {deleteError && (
            <DialogContentText color="error" sx={{ mt: 2 }}>
              {deleteError}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Löschen…' : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
